import CheckIcon from "@mui/icons-material/Check";
import ErrorIcon from "@mui/icons-material/Error";
import { Box, Button, Divider, IconButton, Modal, Stack, Typography } from "@mui/material";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import "./translations";

import { CloseIcon, RealEstatePriceIndexIcon } from "@/assets";

const styles = {
  modalContainer: {
    maxWidth: "calc(100% - 32px)",
    maxHeight: "calc(100% - 32px)",
    overflowY: "auto",
    bgcolor: "background.paper",
    borderRadius: 2,
    outline: "none",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  headerText: {
    fontWeight: 600,
    fontSize: "20px",
    lineHeight: "30px",
    color: "text.primary",
  },
  contentText: {
    fontSize: "18px",
    lineHeight: "28px",
    color: "text.primary",
  },
};

export type PaymentModalState =
  | "quotaConfirmation"
  | "quotaExhausted"
  | "coinConfirmation"
  | "insufficientCoins"
  | null;

const titleKeys = {
  quotaConfirmation: "quotaDeductionModal.header",
  quotaExhausted: "allQuotaUsedModal.header",
  coinConfirmation: "shopwareValuationModal.confirmTitle",
  insufficientCoins: "shopwareValuationModal.insufficientTitle",
} as const;

interface PaymentModelModalProps {
  variant: PaymentModalState;
  onCancel: () => void;
  onConfirm: () => void;
  coinsShopUrl?: string;
  isSubmitting?: boolean;
}

export const PaymentModelModal = ({
  variant,
  onCancel,
  onConfirm,
  coinsShopUrl,
  isSubmitting = false,
}: PaymentModelModalProps): JSX.Element | null => {
  const { t } = useTranslation("ValuationDetailsPage");

  if (variant === null) return null;

  const isQuotaConfirmation = variant === "quotaConfirmation";
  const isQuotaExhausted = variant === "quotaExhausted";
  const isConfirmation = isQuotaConfirmation || variant === "coinConfirmation";

  return (
    <Modal
      open
      onClose={!isQuotaConfirmation && !isQuotaExhausted && !isSubmitting ? onCancel : undefined}
      sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      <Box
        sx={{
          ...styles.modalContainer,
          width: isQuotaConfirmation ? "830px" : "600px",
          boxShadow: 24,
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-model-modal-title"
        aria-describedby="payment-model-modal-text"
        data-cy={
          isQuotaConfirmation
            ? "valuation-package-quota-deduction-modal"
            : isQuotaExhausted
              ? "valuation-package-quota-exhausted-modal"
              : "valuation-package-shopware-valuation-modal"
        }
      >
        <Box sx={{ ...styles.header, padding: isQuotaExhausted ? "30px 30px 12px" : "30px" }}>
          <Stack direction="row" spacing="15px" alignItems="center">
            {isConfirmation ? (
              <RealEstatePriceIndexIcon sx={{ color: "primary.main", fontSize: "26px" }} />
            ) : (
              <ErrorIcon sx={{ color: "error.dark", fontSize: "26px" }} />
            )}
            <Typography id="payment-model-modal-title" sx={styles.headerText}>
              {t(titleKeys[variant])}
            </Typography>
          </Stack>
          <IconButton onClick={onCancel} disabled={isSubmitting} aria-label={t("shopwareValuationModal.close")}>
            <CloseIcon sx={{ fontSize: "18px" }} color="primary" />
          </IconButton>
        </Box>
        <Divider />
        <Box
          id="payment-model-modal-text"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: isQuotaExhausted ? "30px 30px 60px" : "30px",
          }}
        >
          {isQuotaConfirmation ? (
            <>
              <Typography sx={{ ...styles.contentText, fontWeight: 600, paddingBottom: "30px" }}>
                {t("quotaDeductionModal.textTitle")}
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" pl={2}>
                <CheckIcon color="primary" />
                <Typography sx={styles.contentText}>{t("quotaDeductionModal.text1")}</Typography>
              </Stack>
              <Stack direction="row" spacing={2} alignItems="center" pl={2}>
                <CheckIcon color="primary" />
                <Typography sx={styles.contentText}>{t("quotaDeductionModal.text2")}</Typography>
              </Stack>
            </>
          ) : isQuotaExhausted ? (
            <Typography sx={styles.contentText}>
              <strong style={{ display: "block", paddingBottom: "8px" }}>{t("allQuotaUsedModal.textTitle")}</strong>
              {t("allQuotaUsedModal.text1")}
              <br />
              {t("allQuotaUsedModal.text2")}
            </Typography>
          ) : (
            <Typography sx={styles.contentText}>
              {t(isConfirmation ? "shopwareValuationModal.confirmText" : "shopwareValuationModal.insufficientText")}
            </Typography>
          )}
        </Box>
        {!isQuotaExhausted && (
          <Stack
            spacing="30px"
            direction={{ xs: "column", sm: "row" }}
            justifyContent={"center"}
            sx={{
              padding: "30px",
              "& .MuiButton-root": isQuotaConfirmation ? { minHeight: "45px" } : undefined,
            }}
          >
            <Button
              onClick={onCancel}
              color="secondary"
              variant="contained"
              disabled={isSubmitting}
              sx={isQuotaConfirmation ? { minWidth: "120px" } : undefined}
              data-cy={isQuotaConfirmation ? undefined : "valuation-package-shopware-cancel"}
            >
              {t(isQuotaConfirmation ? "quotaDeductionModal.back" : "shopwareValuationModal.cancel")}
            </Button>
            {isConfirmation ? (
              <Button
                autoFocus
                onClick={onConfirm}
                variant="contained"
                color="primary"
                disabled={isSubmitting}
                data-cy={
                  isQuotaConfirmation
                    ? "valuation-package-quota-deduction-continue"
                    : "valuation-package-shopware-confirm"
                }
              >
                {t(isQuotaConfirmation ? "quotaDeductionModal.valuateNow" : "shopwareValuationModal.confirm")}
              </Button>
            ) : (
              <Button
                autoFocus
                component="a"
                href={coinsShopUrl}
                onClick={onCancel}
                target="_blank"
                rel="noopener noreferrer"
                variant="contained"
                color="primary"
                disabled={!coinsShopUrl || isSubmitting}
                data-cy="valuation-package-shopware-buy-coins"
              >
                {t("shopwareValuationModal.buyCoins")}
              </Button>
            )}
          </Stack>
        )}
      </Box>
    </Modal>
  );
};
